import React, { useEffect, useRef } from 'react';
import { IHeadToHeadMatch } from '../models/head-to-head';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { LinkButton } from './link-button';
import { Muted, P, Text } from './text';

interface IProps {
	matches: IHeadToHeadMatch[];
}

/**
 * HeadToHeadMatchList Component
 *
 * Displays a list of historical matches between two teams.
 * Shows the last 5 matches with venue, teams, date, time, and scores.
 * Uses the same UI styling as SquadMatchesList for consistency.
 */
export default function HeadToHeadMatchList({ matches }: IProps) {
	const observerRef = useRef<IntersectionObserver | null>(null);

	useEffect(() => {
		// Create the observer for fade-in animation
		observerRef.current = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add('animate-fade-in-from-bottom');
						// Once animation is added, we can stop observing
						observerRef.current?.unobserve(entry.target);
					}
				});
			},
			{
				root: null,
				rootMargin: '0px',
				threshold: 0.1,
			},
		);

		// Observe all match cards
		const matchCards = document.querySelectorAll('.match-card');
		matchCards.forEach((card) => {
			observerRef.current?.observe(card);
		});

		// Cleanup
		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, [matches]);

	// Handle empty state
	if (!matches || matches.length === 0) {
		return (
			<div className="text-center py-8 text-gray-500">
				<p>These teams have not met in this competition</p>
			</div>
		);
	}

	// Display only the first 5 matches
	const displayMatches = matches.slice(0, 5);

	return (
		<div className="space-y-4">
			{displayMatches.map((match) => {
				const hasScores = match.scores?.away != null && match.scores?.home != null;

				const formatDate = (date: string) => format(parseISO(date), 'dd-MM-yyyy', { locale: nl });
				const stripCharactersFromSlug = (slug: string) => slug.replace('vs-', '').slice(0, -10);

				const pastMatchUrl = `/video/samenvatting-${stripCharactersFromSlug(match.slug)}-${formatDate(
					match.date,
				)}`;

				// Show button if there are scores or if the match has a ticket URL
				const showButton =
					(hasScores && match.slug) || (!hasScores && match.homeContestant.contestantTicketURL);
				const buttonHref = (hasScores ? `${pastMatchUrl}` : match.homeContestant.contestantTicketURL) ?? '';
				const buttonText = hasScores ? 'Samenvatting' : 'koop kaarten';

				return (
					<div key={match.slug}>
						<Muted className="mb-1 px-1">{format(parseISO(match.date), 'Y', { locale: nl })}</Muted>
						<article className="match-card grid grid-cols-[130px_1fr_200px] items-center bg-white border-b-4 border-red-500 h-[90px] opacity-0 translate-y-4">
							{/* Left side - Date and Time */}
							<div className="py-6 px-5 flex flex-col">
								<P className="text-xs  font-semibold !font-mono">
									{/* Slice first 5 chars to remove seconds from time string (e.g. "20:00:00" -> "20:00") */}
									<time dateTime={match.time}>{match.time.slice(0, 5)}</time>
								</P>
								<time dateTime={match.date} className="text-lg uppercase font-semibold !font-mono">
									{format(parseISO(match.date), 'd MMM', { locale: nl })}
								</time>
							</div>

							{/* Center content - Teams and Scores */}
							<div className="flex justify-center items-center gap-4 relative h-full">
								{/* Home Team */}
								<div className="flex flex-1 justify-end items-center gap-2">
									<Text className="text-md !font-bold uppercase hidden lg:block">
										{match.homeContestant.contestantClubName}
									</Text>
									<Text className="text-md !font-bold uppercase xs:block lg:hidden">
										{match.homeContestant.contestantCode}
									</Text>
									{match.homeContestant.contestantLogo && (
										<img
											src={match.homeContestant.contestantLogo || undefined}
											alt={`${match.homeContestant.contestantClubName} logo`}
											className="w-12 h-12"
										/>
									)}
								</div>

								{/* Center - Scores or Dash */}
								{hasScores ? (
									<p
										className="text-2xl text-red-500 uppercase font-semibold !font-mono text-center"
										aria-label={`Score: ${match.scores?.home}-${match.scores?.away}`}
									>
										{match.scores?.home}-{match.scores?.away}
									</p>
								) : (
									<p className="text-2xl font-bold " aria-label="Match not played yet">
										-
									</p>
								)}

								{/* Channel logo */}
								{!!match.channel && (
									<div className="w-14 absolute top-0 left-1/2 -translate-x-1/2">
										<img
											src={`/images/channel_${match.channel}.png`}
											alt={`${match.channel} channel logo`}
										/>
									</div>
								)}

								{/* Away Team */}
								<div className="flex flex-1 items-center gap-2">
									{match.awayContestant.contestantLogo && (
										<img
											src={match.awayContestant.contestantLogo || undefined}
											alt={`${match.awayContestant.contestantClubName} logo`}
											className="w-12 h-12"
										/>
									)}
									<Text className="!text-md !font-bold uppercase hidden lg:block">
										{match.awayContestant.contestantClubName}
									</Text>
									<Text className="!text-md !font-bold uppercase xs:block lg:hidden">
										{match.awayContestant.contestantCode}
									</Text>
								</div>
							</div>

							{/* Right side - Samenvatting Button */}
							<div className="py-6 px-4 flex items-center gap-4 justify-end">
								{showButton && (
									<LinkButton href={buttonHref} size="small" variant="primary" className="uppercase">
										{buttonText}
									</LinkButton>
								)}
							</div>
						</article>
					</div>
				);
			})}
		</div>
	);
}
